import { useSubscriptionStore } from './subscriptionStore';
import { useCallback } from 'react';

// Subscription selectors - refactored to avoid infinite loop
export const useSubscriptionOptions = () => {
  const subscriptionType = useSubscriptionStore(state => state.subscriptionType);
  const subscriptionLevel = useSubscriptionStore(state => state.subscriptionLevel);
  const paymentMethod = useSubscriptionStore(state => state.paymentMethod);
  const paymentType = useSubscriptionStore(state => state.paymentType);
  const setSubscriptionType = useSubscriptionStore(state => state.setSubscriptionType);
  const setSubscriptionLevel = useSubscriptionStore(state => state.setSubscriptionLevel);
  const setPaymentMethod = useSubscriptionStore(state => state.setPaymentMethod);
  const setPaymentType = useSubscriptionStore(state => state.setPaymentType);
  const updateSubscriptionState = useSubscriptionStore(state => state.updateSubscriptionState);
  const setShowPromptPayInfo = useSubscriptionStore(state => state.setShowPromptPayInfo);
  
  return {
    subscriptionType,
    subscriptionLevel,
    paymentMethod,
    paymentType,
    setSubscriptionType,
    setSubscriptionLevel,
    setPaymentMethod,
    setPaymentType,
    updateSubscriptionState,
    setShowPromptPayInfo
  };
};

// Currency selectors
export const useCurrencyState = () => {
  const currency = useSubscriptionStore(state => state.currency);
  const availableCurrencies = useSubscriptionStore(state => state.availableCurrencies);
  const setCurrency = useSubscriptionStore(state => state.setCurrency);
  
  return {
    currency,
    availableCurrencies,
    setCurrency
  };
};

// UI state selectors - refactored to avoid infinite loop
export const useUIState = () => {
  const loading = useSubscriptionStore(state => state.loading);
  const error = useSubscriptionStore(state => state.error);
  const showPromptPayInfo = useSubscriptionStore(state => state.showPromptPayInfo);
  const showAddressModal = useSubscriptionStore(state => state.showAddressModal);
  const hasAttemptedAddressSelection = useSubscriptionStore(state => state.hasAttemptedAddressSelection);
  const showManualAddressPrompt = useSubscriptionStore(state => state.showManualAddressPrompt);
  const setLoading = useSubscriptionStore(state => state.setLoading);
  const setError = useSubscriptionStore(state => state.setError);
  const setShowPromptPayInfo = useSubscriptionStore(state => state.setShowPromptPayInfo);
  const setShowAddressModal = useSubscriptionStore(state => state.setShowAddressModal);
  const setHasAttemptedAddressSelection = useSubscriptionStore(state => state.setHasAttemptedAddressSelection);
  const setShowManualAddressPrompt = useSubscriptionStore(state => state.setShowManualAddressPrompt);
  
  return {
    loading,
    error,
    showPromptPayInfo,
    showAddressModal,
    hasAttemptedAddressSelection,
    showManualAddressPrompt,
    setLoading,
    setError,
    setShowPromptPayInfo,
    setShowAddressModal,
    setHasAttemptedAddressSelection,
    setShowManualAddressPrompt
  };
};

// Plan details selector - refactored to avoid infinite loop
export const usePlanDetails = () => {
  const planDetails = useSubscriptionStore(state => state.planDetails);
  const setPlanDetails = useSubscriptionStore(state => state.setPlanDetails);
  
  return { planDetails, setPlanDetails };
};

// Gift state selectors - refactored to avoid infinite loop
export const useGiftState = () => {
  const isGift = useSubscriptionStore(state => state.isGift);
  const recipientName = useSubscriptionStore(state => state.recipientName);
  const recipientPhone = useSubscriptionStore(state => state.recipientPhone);
  const giftMessage = useSubscriptionStore(state => state.giftMessage);
  const senderName = useSubscriptionStore(state => state.senderName);
  const senderPhone = useSubscriptionStore(state => state.senderPhone);
  const updateGiftState = useSubscriptionStore(state => state.updateGiftState);
  
  return {
    isGift,
    recipientName,
    recipientPhone,
    giftMessage,
    senderName,
    senderPhone,
    updateGiftState
  };
};

// Schedule state selectors - refactored to avoid infinite loop
export const useScheduleState = () => {
  const selectedTimeSlots = useSubscriptionStore(state => state.selectedTimeSlots);
  const selectedDate = useSubscriptionStore(state => state.selectedDate);
  const weeklyDay = useSubscriptionStore(state => state.weeklyDay);
  const updateScheduleState = useSubscriptionStore(state => state.updateScheduleState);
  
  return {
    selectedTimeSlots,
    selectedDate,
    weeklyDay,
    updateScheduleState
  };
};

// Preferences state selector
export const usePreferencesState = () => {
  const preferences = useSubscriptionStore(state => state.preferences);
  const updatePreferences = useSubscriptionStore(state => state.updatePreferences);
  
  return {
    preferences,
    updatePreferences
  };
};

// Promotion selectors - refactored to avoid infinite loop
export const usePromotionState = () => {
  const promotionCode = useSubscriptionStore(state => state.promotionCode);
  const promotionDetails = useSubscriptionStore(state => state.promotionDetails);
  const setPromotionCode = useSubscriptionStore(state => state.setPromotionCode);
  const setPromotionDetails = useSubscriptionStore(state => state.setPromotionDetails);
  
  return {
    promotionCode,
    promotionDetails,
    setPromotionCode,
    setPromotionDetails
  };
};

// Toast flags selectors - refactored to avoid infinite loop
export const useToastFlags = () => {
  const hasShownScheduleToast = useSubscriptionStore(state => state.hasShownScheduleToast);
  const hasShownPromoToast = useSubscriptionStore(state => state.hasShownPromoToast);
  const hasShownPaymentSwitchToast = useSubscriptionStore(state => state.hasShownPaymentSwitchToast);
  const setHasShownScheduleToast = useSubscriptionStore(state => state.setHasShownScheduleToast);
  const setHasShownPromoToast = useSubscriptionStore(state => state.setHasShownPromoToast);
  const setHasShownPaymentSwitchToast = useSubscriptionStore(state => state.setHasShownPaymentSwitchToast);
  
  return {
    hasShownScheduleToast,
    hasShownPromoToast,
    hasShownPaymentSwitchToast,
    setHasShownScheduleToast,
    setHasShownPromoToast,
    setHasShownPaymentSwitchToast
  };
};

// Price calculation selector - refactored to avoid infinite loop
export const usePriceCalculation = () => {
  // Get only the state values needed for calculation
  const subscriptionLevel = useSubscriptionStore(state => state.subscriptionLevel);
  const subscriptionType = useSubscriptionStore(state => state.subscriptionType);
  const paymentType = useSubscriptionStore(state => state.paymentType);
  const planDetails = useSubscriptionStore(state => state.planDetails);
  const promotionCode = useSubscriptionStore(state => state.promotionCode);
  const promotionDetails = useSubscriptionStore(state => state.promotionDetails);
  const currency = useSubscriptionStore(state => state.currency);
  const serviceType = useSubscriptionStore(state => state.serviceType);
  
  // This function will be stable across renders
  const calculatePrice = useCallback((basePrice: number) => {
    if (!planDetails) return basePrice;
    
    // For trial subscriptions, use the base price directly without any additional discounts
    if (subscriptionType === 'trial') {
      return basePrice;
    }
    
    // Determine if we should use USD pricing
    const useUSD = currency === 'usd';
    
    // Get proper base price according to currency
    let price = basePrice;
    if (useUSD && subscriptionLevel === 'standard' && planDetails.standard?.price_usd) {
      price = planDetails.standard.price_usd;
    } else if (useUSD && subscriptionLevel === 'premium' && planDetails.premium?.price_usd) {
      price = planDetails.premium.price_usd;
    }
    
    // Apply any service type specific pricing adjustments
    // For example, fruit subscriptions might have different pricing
    if (serviceType === 'fruit') {
      // Apply a 10% premium for fruit subscriptions
      price = price * 1.1;
    }
    
    // Apply yearly discount if applicable
    if (subscriptionType === 'yearly') {
      price = price * 12;
      
      // Apply combined discount for yearly subscriptions
      if (paymentType === 'subscription') {
        // Combined yearly + subscription discount: 15% total discount
        price = price * 0.85;
      } else {
        // Only yearly discount: 10% discount
        price = price * 0.9;
      }
    } else {
      // For monthly subscriptions, apply only subscription payment discount
      if (paymentType === 'subscription') {
        price = price * 0.95; // 5% discount
      }
    }
    
    // Price before promotion code
    const priceBeforePromotion = price;
    
    // Apply promotion code if valid and loaded
    if (promotionCode && promotionDetails.isValid && promotionDetails.isLoaded) {
      // Check if the code has minimum amount requirement with a small EPSILON to handle floating point precision
      const EPSILON = 0.001;
      if (promotionDetails.minimumAmount && priceBeforePromotion + EPSILON < promotionDetails.minimumAmount) {
        // Skip applying the promotion as it doesn't meet minimum amount
        return priceBeforePromotion;
      }
      
      // Apply percentage discount
      if (promotionDetails.percentOff > 0) {
        price = price * (1 - promotionDetails.percentOff / 100);
      }
      
      // Apply fixed amount discount only if currency matches
      if (promotionDetails.amountOff > 0 && 
          (!promotionDetails.currency || 
           (promotionDetails.currency === currency))) {
        price = Math.max(0, price - promotionDetails.amountOff);
      }
    }
    
    // Return the final calculated price, rounded to 2 decimal places
    return Math.round(price * 100) / 100;
  }, [subscriptionLevel, subscriptionType, paymentType, planDetails, promotionCode, promotionDetails, currency, serviceType]);
  
  return { calculatePrice };
}; 